import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FaHome, FaBuilding } from "react-icons/fa";
import { BiSolidContact } from "react-icons/bi";
import { LuNotebookText } from "react-icons/lu";

const ContactPage = () => {
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [successMessage, setSuccessMessage] = useState("");

    const siteKey = "6LeJHMAqAAAAAA-qqSxwQWZO22msa33a2_wRPN_o"; // Replace with your reCAPTCHA v3 site key

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrorMessage("");
        setSuccessMessage("");

        const formData = new FormData(e.target);

        try {
            // Execute reCAPTCHA v3 to get a token
            const token = await window.grecaptcha.execute(siteKey, { action: "submit" });
            formData.append("recaptcha", token); // Append the token to the form data

            const response = await fetch("forms/contact.php", {
                method: "POST",
                body: formData,
            });

            if (!response.ok) {
                throw new Error("Failed to send message");
            }

            const result = await response.text();
            if (result.includes("sent")) {
                setSuccessMessage("Your message has been sent. Thank you!");
                setTimeout(() => {
                    setSuccessMessage("");
                }, 5000);
            } else {
                throw new Error("Failed to send message");
            }
        } catch (error) {
            setErrorMessage("There was an error sending your message. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // Load the reCAPTCHA script dynamically
        const script = document.createElement("script");
        script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
        script.async = true;
        document.body.appendChild(script);

        return () => {
            document.body.removeChild(script);
        };
    }, []);

    return (
        <section className="bg-white dark:bg-gray-900 relative min-h-screen">
            {/* Home Button */}
            <div className="absolute top-4 left-4">
                <Link to="/">
                    <button className="p-2 text-gray-800 bg-white rounded-full shadow-lg hover:bg-gray-200 transition">
                        <FaHome className="w-6 h-6" />
                    </button>
                </Link>
            </div>

            <div className="py-8 lg:py-16 px-4 mx-auto max-w-screen-lg">
                <h2 className="mb-4 text-4xl tracking-tight font-extrabold text-center text-gray-900 dark:text-white">
                    Contact Us
                </h2>
                <p className="mb-8 lg:mb-16 font-light text-center text-gray-500 dark:text-gray-400 sm:text-xl">
                    We’d love to hear from you! Whether you have a question, need assistance, or want to work with us, feel free to reach out.
                </p>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Contact Form */}
                    <div className="bg-white dark:bg-gray-800 p-6 sm:p-8 shadow-lg rounded-lg">
                        <h3 className="text-2xl font-semibold text-gray-800 dark:text-white mb-6">
                            Get In Touch
                        </h3>
                        <form method="post" action="forms/contact.php" id="contact-form" onSubmit={handleSubmit} className="space-y-6">
                            <div>
                                <label htmlFor="name" className="block mb-2 text-sm font-medium text-gray-900 dark:text-gray-300">
                                    Your Name
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    className="block p-3 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                                    placeholder="Your name"
                                    required
                                />
                            </div>
                            <div>
                                <label htmlFor="email" className="block mb-2 text-sm font-medium text-gray-900 dark:text-gray-300">
                                    Your Email
                                </label>
                                <input
                                    type="email"
                                    name="email"
                                    className="shadow-sm bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                                    placeholder="name@gmail.com"
                                    required
                                />
                            </div>
                            <div>
                                <label htmlFor="subject" className="block mb-2 text-sm font-medium text-gray-900 dark:text-gray-300">
                                    Subject
                                </label>
                                <input
                                    type="text"
                                    name="subject"
                                    className="block p-3 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                                    placeholder="Let us know how we can help you"
                                    required
                                />
                            </div>
                            <div>
                                <label htmlFor="message" className="block mb-2 text-sm font-medium text-gray-900 dark:text-gray-300">
                                    Your Message
                                </label>
                                <textarea
                                    name="message"
                                    rows="6"
                                    className="block p-3 w-full text-sm text-gray-900 bg-gray-50 rounded-lg shadow-sm border border-gray-300 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                                    placeholder="Leave a message"
                                    required
                                ></textarea>
                            </div>
                            <div className="my-3">
                                {loading && <div className="loading text-blue-500">Loading...</div>}
                                {errorMessage && <div className="error-message text-red-500">{errorMessage}</div>}
                                {successMessage && <div className="sent-message text-green-500">{successMessage}</div>}
                            </div>
                            <div>
                                <button
                                    type="submit"
                                    className="py-3 px-5 text-sm font-medium text-center text-white rounded-lg bg-black sm:w-fit hover:bg-gray-800 focus:ring-4 focus:outline-none focus:ring-gray-500 dark:bg-black dark:hover:bg-gray-700 dark:focus:ring-gray-600"
                                >
                                    Send Message
                                </button>
                            </div>
                            {/* Inline Attribution for Google reCAPTCHA */}
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-6">
                                This site is protected by reCAPTCHA and the Google{" "}
                                <a
                                    href="https://policies.google.com/privacy"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary-500 hover:underline"
                                >
                                    Privacy Policy
                                </a>{" "}
                                and{" "}
                                <a
                                    href="https://policies.google.com/terms"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary-500 hover:underline"
                                >
                                    Terms of Service
                                </a>{" "}
                                apply.
                            </p>
                        </form>
                    </div>

                    {/* Company Info */}
                    <div className="bg-gray-900 dark:bg-gray-200 p-6 sm:p-8 shadow-lg rounded-lg">
                        <h3 className="text-2xl font-semibold text-white dark:text-gray-800 mb-6">
                            Company Info
                        </h3>
                        <div className="flex items-center mb-6">
                            <FaBuilding className="text-white dark:text-gray-800 w-8 h-8 mr-4" />
                            <div>
                                <p className="text-gray-300 dark:text-gray-600">BJELUTAK d.o.o.</p>
                                <p className="text-gray-300 dark:text-gray-600">Hrvatskih velikana 8, Supetar</p>
                            </div>
                        </div>
                        <hr className="my-6 border-gray-700 dark:border-gray-300" />
                        <div className="flex items-center mb-6">
                            <LuNotebookText className="text-white dark:text-gray-800 w-8 h-8 mr-4" />
                            <div>
                                <p className="text-gray-300 dark:text-gray-600">OIB: 51060333227</p>
                                <p className="text-gray-300 dark:text-gray-600">MB: 05312655</p>
                            </div>
                        </div>
                        <hr className="my-6 border-gray-700 dark:border-gray-300" />
                        <div className="flex items-center">
                            <BiSolidContact className="text-white dark:text-gray-800 w-8 h-8 mr-4" />
                            <div>
                                <p className="text-gray-300 dark:text-gray-600">Email: info@bjelutak.hr</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ContactPage;
